// İSG uzmanı portalı — Kontrol aşaması kuyruğu (api-contract §31.5). Kullanıcının departman(lar)ının
// (İSG/Hekim/İK — rolünden türetilir) **onayı bekleyen** izinleri (durum `DepartmentReview`). Uç düz
// `WorkPermitDto[]` döndürür (departmanı echo etmez); departman etiketi başlıkta viewer rollerinden
// türetilir. Satıra tıklama → Kontrol detayı. İzin listesi (§14) ekranından AYRIDIR: bu yalnız
// departman onayı bekleyenler.
//
// S33 Slice 7 fix — bu sayfa Slice 4'te GÖZDEN KAÇMIŞTI (final QA defekt #3): diğer tüm liste
// sayfalarıyla AYNI Carbon anatomisine (Breadcrumb → PageHeader → Card(TableToolbar → Table →
// Pagination), skeleton satırlar) geçirildi — emsal `ContractorsListPage.tsx` (ayrı bir Actions
// sütunu/⋮ menüsü YOK, bu listede satır başına ikincil/yıkıcı bir eylem yok, tek eylem "aç" ve o
// zaten başlık hücresinin kendisi — gerçek bir `Link`, önceki `<tr onClick>` DEĞİL).

import {
  Breadcrumb,
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLink,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  Tag,
  currentCulture,
  departmentReviewApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { ListChecks } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPermitStatusPresentation } from '../../components/permits/permitEnums';
import { departmentReviewKeys } from '../../lib/departmentReviewKeys';
import { useViewerDepartments } from '../../lib/departmentRoles';
import { formatDateRange } from '../../lib/format';
import { useTableDensity } from '../../lib/uiPrefs';

const PAGE_SIZE = 20;

export function DepartmentReviewQueuePage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const [page, setPage] = useState(1);
  const viewerDepartments = useViewerDepartments();

  const params = { page, pageSize: PAGE_SIZE };
  const query = useQuery({
    queryKey: departmentReviewKeys.listParams(params),
    queryFn: () => departmentReviewApi.listDepartmentReview(params),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('departmentReview.title') }]} />
      <PageHeader
        title={t('departmentReview.title')}
        helperText={t('departmentReview.subtitle')}
        tags={viewerDepartments.map((d) => (
          <Tag key={d} color="blue">
            {t(`permits.department.${d}`)}
          </Tag>
        ))}
      />

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('departmentReview.count', { count: query.data.filteredTotal })
                : undefined
            }
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
          />

          <Table caption={t('departmentReview.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('departmentReview.col.permit')}</TableHead>
                <TableHead>{t('departmentReview.col.contractor')}</TableHead>
                <TableHead>{t('departmentReview.col.validity')}</TableHead>
                <TableHead>{t('departmentReview.col.permitStatus')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={4} />
              ) : query.isError ? (
                <TableMessageRow colSpan={4}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={4}>
                  <EmptyState icon={ListChecks} title={t('departmentReview.empty')} />
                </TableMessageRow>
              ) : (
                rows.map((permit) => (
                  <TableRow key={permit.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = başlık hücresi (S33 Slice 4 QA fix, defekt #4 —
                          `ContractorsListPage.tsx` emsali). Bu listede ayrı bir Actions
                          sütunu/⋮ menüsü yok: satır başına ikincil/yıkıcı eylem yok. */}
                      <TableLink to={`/department-review/${permit.uuid}`} className="block">
                        {permit.title}
                      </TableLink>
                      {permit.permitTypeName ? (
                        <span className="block text-xs text-muted">{permit.permitTypeName}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted">{permit.contractorName ?? '—'}</TableCell>
                    <TableCell className="text-muted tnum">
                      {formatDateRange(permit.validityStart, permit.validityEnd, culture)}
                    </TableCell>
                    <TableCell>
                      <Tag color={getPermitStatusPresentation(permit.status).tagColor}>
                        {t(getPermitStatusPresentation(permit.status).labelKey)}
                      </Tag>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('departmentReview.pagination.nav'),
                previous: t('departmentReview.pagination.previous'),
                next: t('departmentReview.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
